import HologardeBackground from '@assets/hologarde-background.jpg';
import { IcConfirmedBold, IcError } from '@assets/icons/24x24';
import {
  Box,
  Button,
  Center,
  Field as ChakraField,
  Fieldset,
  Flex,
  HStack,
  Icon,
  Image,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Form } from '@components/ui/Form';
import { PasswordInput } from '@components/ui/password-input';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppDispatch, useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { userLogout } from '@redux/authent/authent.reducer';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import {
  useCheckPersonalPreviousPasswordsMutation,
  useGetMeQuery,
  usePatchPersonalPasswordMutation,
} from '@services/authent/user.api';
import { createErrorMessage, isFirstConnectionExceptionPayload } from '@utils/common.utils';
import { changePassword, isValidComposition, isValidRepetition, isValidSize } from '@validations/user.schema';
import { Field, FieldInputProps, Formik, FormikProps } from 'formik';
import { debounce } from 'lodash';
import { ReactNode, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FirstConnectionExceptionPayload } from '@/types/commons/commons.types';

import ErrorsContainer from './common/form/ErrorsContainer';

type Props = {
  children: ReactNode;
};

export default function UserInit({ children }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const activeUser = useAppSelector(selectActiveUser);
  const validationSchema = useValidation(changePassword);

  const { isLoading: isLoadingGetMe, isError: isErrorGetMe, error: errorGetMe } = useGetMeQuery();
  const [
    patchPassword,
    { isLoading: isLoadingPatchPassword, isError: isErrorPatchPassword, error: errorPatchPassword },
  ] = usePatchPersonalPasswordMutation();
  const [checkPassword, { data: checkPasswordResult, isLoading: isLoadingCheckPassword }] =
    useCheckPersonalPreviousPasswordsMutation();

  function isValidPassword(
    currentPassword: string,
    newPassword: string,
    values: FirstConnectionExceptionPayload,
  ): boolean {
    const { first_name, name, email, login } = values.data;
    return (
      isValidSize(newPassword) &&
      isValidComposition(newPassword) &&
      isValidRepetition(newPassword, [first_name, name, email.split('@')[0], login]) &&
      currentPassword !== newPassword
    );
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCheckPassword = useCallback(
    debounce((value: { newPassword: string }) => {
      checkPassword(value);
    }, 500),
    [],
  );

  const ValidIcon = <Icon height="20px" width="20px" as={IcConfirmedBold} color="green.500" />;
  const InvalidIcon = <Icon height="20px" width="20px" as={IcError} color="red" />;
  const DefaultIcon = (
    <Center height="20px" width="20px" flexShrink={0}>
      {isLoadingCheckPassword ? (
        <Spinner boxSize="20px" color="neutral.white" />
      ) : (
        <Box flexShrink={0} borderRadius="50%" boxSize="8px" backgroundColor="neutral.500"></Box>
      )}
    </Center>
  );

  if (activeUser.id !== -1) {
    return children;
  }

  if (isErrorGetMe && !isFirstConnectionExceptionPayload(errorGetMe)) {
    dispatch(userLogout());
    createToast(createErrorMessage(errorGetMe), ToastStatusEnum.ERROR);
    return null;
  }

  /*console.log(`LOADING -> ${isLoadingGetMe}`);
  console.log(`ERROR -> ${errorGetMe}`);
  console.log(`INTER -> ${isLoadingGetMe || !errorGetMe}`);
  console.log(`FINAL -> ${isLoadingGetMe || !errorGetMe || !isFirstConnectionExceptionPayload(errorGetMe)}`);*/

  return (
    <Flex width="100%" height="100%" alignContent="center" justifyContent="center">
      <Image src={HologardeBackground} fit="cover" height="100%" width="100%" position="absolute" />
      {isLoadingGetMe || !errorGetMe || !isFirstConnectionExceptionPayload(errorGetMe) ? (
        <Center>
          <Spinner size="xl" color="neutral.white" />
        </Center>
      ) : (
        <VStack
          position="fixed"
          right={0}
          alignItems="start"
          gap={5}
          width="424px"
          height="100%"
          padding={8}
          paddingTop={9}
          backgroundColor="neutral.900-op30"
        >
          <Text textStyle="xl" letterSpacing="4px" fontWeight="400">
            <FormattedMessage id={`authent.firstLogin.title.${errorGetMe.data.login_state}`} />
          </Text>
          <Formik
            initialValues={{
              currentPassword: '',
              newPassword: '',
              passwordConfirmation: '',
            }}
            onSubmit={(values) =>
              patchPassword({
                newPassword: values.newPassword,
                currentPassword: values.currentPassword,
              })
            }
            validationSchema={validationSchema}
          >
            {({ errors, touched, isValid, values, handleSubmit, setFieldValue }) => (
              <Fieldset.Root invalid={isErrorPatchPassword}>
                <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <VStack alignItems="start" width="100%" gap={3}>
                    <Text textStyle="md">
                      <FormattedMessage id={`authent.firstLogin.subTitle.${errorGetMe.data.login_state}`} />
                    </Text>
                    <Text textStyle="md">
                      <FormattedMessage id="authent.firstLogin.description" />
                    </Text>
                    <VStack alignItems="start" width="100%" gap={1}>
                      <HStack gap={2} alignItems="center">
                        {touched.newPassword || values.newPassword !== ''
                          ? isValidSize(values.newPassword)
                            ? ValidIcon
                            : InvalidIcon
                          : DefaultIcon}
                        <Text>
                          <FormattedMessage id="authent.firstLogin.size" />
                        </Text>
                      </HStack>
                      <HStack gap={2} alignItems="center">
                        {checkPasswordResult === undefined
                          ? DefaultIcon
                          : checkPasswordResult && values.currentPassword !== values.newPassword
                            ? ValidIcon
                            : InvalidIcon}
                        <Text>
                          <FormattedMessage id="authent.firstLogin.oldPassword" />
                        </Text>
                      </HStack>
                      <HStack gap={2} alignItems="start">
                        {touched.newPassword || values.newPassword !== ''
                          ? isValidRepetition(values.newPassword, [
                              errorGetMe.data.first_name,
                              errorGetMe.data.name,
                              errorGetMe.data.email.split('@')[0],
                              errorGetMe.data.login,
                            ])
                            ? ValidIcon
                            : InvalidIcon
                          : DefaultIcon}
                        <Text>
                          <FormattedMessage id="authent.firstLogin.repetition" />
                        </Text>
                      </HStack>
                      <HStack gap={2} alignItems="start">
                        {touched.newPassword || values.newPassword !== ''
                          ? isValidComposition(values.newPassword)
                            ? ValidIcon
                            : InvalidIcon
                          : DefaultIcon}
                        <Text>
                          <FormattedMessage id="authent.firstLogin.composition" />
                        </Text>
                      </HStack>
                    </VStack>
                    <ChakraField.Root required invalid={touched.currentPassword && !!errors.currentPassword}>
                      <ChakraField.Label>
                        <Text textStyle="md" lineHeight="20px">
                          <FormattedMessage id="authent.firstLogin.currentPassword" />
                        </Text>
                      </ChakraField.Label>
                      <Field id="currentPassword" name="currentPassword">
                        {({
                          field,
                          form,
                        }: {
                          field: FieldInputProps<string>;
                          form: FormikProps<{ name: string; surname: string }>;
                        }) => (
                          <PasswordInput
                            {...field}
                            showLockIcon
                            onChange={form.handleChange}
                            onBlur={form.handleBlur}
                          />
                        )}
                      </Field>
                    </ChakraField.Root>
                    <ChakraField.Root required invalid={touched.newPassword && !!errors.newPassword}>
                      <ChakraField.Label>
                        <Text textStyle="md" lineHeight="20px">
                          <FormattedMessage id="authent.firstLogin.newPassword" />
                        </Text>
                      </ChakraField.Label>
                      <Field id="newPassword" name="newPassword">
                        {({
                          field,
                          form,
                        }: {
                          field: FieldInputProps<string>;
                          form: FormikProps<{ name: string; surname: string }>;
                        }) => (
                          <PasswordInput
                            {...field}
                            showLockIcon
                            onChange={(event) => {
                              setFieldValue('newPassword', event.target.value);
                              debouncedCheckPassword({ newPassword: event.target.value });
                            }}
                            onBlur={form.handleBlur}
                          />
                        )}
                      </Field>
                    </ChakraField.Root>
                    <ChakraField.Root required invalid={touched.passwordConfirmation && !!errors.passwordConfirmation}>
                      <ChakraField.Label>
                        <Text textStyle="md" lineHeight="20px">
                          <FormattedMessage id="authent.firstLogin.passwordConfirmation" />
                        </Text>
                      </ChakraField.Label>
                      <Field id="passwordConfirmation" name="passwordConfirmation">
                        {({
                          field,
                          form,
                        }: {
                          field: FieldInputProps<string>;
                          form: FormikProps<{ name: string; surname: string }>;
                        }) => (
                          <PasswordInput
                            {...field}
                            showLockIcon
                            onChange={form.handleChange}
                            onBlur={form.handleBlur}
                          />
                        )}
                      </Field>
                    </ChakraField.Root>
                    <Fieldset.ErrorText marginTop={0}>
                      <ErrorsContainer
                        errors={errors}
                        touched={touched}
                        apiError={isErrorPatchPassword ? createErrorMessage(errorPatchPassword) : undefined}
                      />
                    </Fieldset.ErrorText>
                    <Button
                      type="submit"
                      width="100%"
                      size="md"
                      loading={isLoadingPatchPassword}
                      spinner={<Spinner size="lg" color="neutral.white" />}
                      disabled={
                        !isValid ||
                        !isValidPassword(values.currentPassword, values.newPassword, errorGetMe) ||
                        !checkPasswordResult ||
                        isLoadingCheckPassword
                      }
                    >
                      <FormattedMessage id="global.validate" />
                    </Button>
                    <Text color="neutral.200">
                      <FormattedMessage
                        id="authent.firstLogin.help"
                        values={{ number: formatMessage({ id: 'global.adminNumber' }) }}
                      />
                    </Text>
                  </VStack>
                </Form>
              </Fieldset.Root>
            )}
          </Formik>
        </VStack>
      )}
    </Flex>
  );
}
